import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { NODE_TYPE_META } from './nodeTypes';
import { useUIStore } from '@/stores';
import { useCanvasMutations } from '@/hooks/use-canvas-mutations';
import { generateId } from '@/lib/utils';

export function AddNodeButton({ projectId }: { projectId: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const { addNode } = useCanvasMutations(projectId);
  const canEdit = useUIStore((s) => s.canEdit);

  // Viewers get a read-only canvas — no way to add nodes.
  if (!canEdit) return null;

  const handleAdd = (type: string) => {
    const meta = NODE_TYPE_META[type as keyof typeof NODE_TYPE_META];
    const node = {
      id: `${type}-${generateId()}`,
      type,
      data: {
        label: `New ${meta.label}`,
        content: '',
      },
      // Position is owned by the layout engine (graph-layout.ts) — placeholder
      // until layout runs on the next structural change.
      position: { x: 0, y: 0 },
    };
    addNode(node);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Button
        size="sm"
        variant="outline"
        className="bg-background/90 backdrop-blur-sm shadow-xs"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Node
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 bg-background border rounded-md shadow-lg p-1 min-w-[180px] z-50">
          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase">
            Customer
          </div>
          {(['customer', 'job', 'challenge', 'opportunity'] as const).map((type) => (
            <button
              key={type}
              onClick={() => handleAdd(type)}
              className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-accent"
            >
              {NODE_TYPE_META[type].label}
            </button>
          ))}
          <div className="my-1 border-t" />
          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground uppercase">
            Capabilities
          </div>
          <button
            onClick={() => handleAdd('capability')}
            className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-accent"
          >
            {NODE_TYPE_META.capability.label}
          </button>
          <div className="my-1 border-t" />
          <button
            onClick={() => handleAdd('assumption')}
            className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-accent"
          >
            {NODE_TYPE_META.assumption.label}
          </button>
        </div>
      )}
    </div>
  );
}
